<!-- Copyright (c) 2023, Alix Mougenot (Au pied des Arbres), alix.mougenot@gmail.com -->
<!-- See license file. -->
<script setup lang="ts">
import {
  caseStatusOptions,
  caseSubStatusOptions,
  caseSubTypeOptions,
  caseTypeOptions,
  diff,
  type Case,
  type OpenMode,
  type CaseCreation,
} from "@/types";
import { caseCopy } from "@/types";
import { computed, ref, inject, type Ref, watch } from "vue";
import { MDBTextarea } from "mdb-vue-ui-kit";
import { useRouter } from "vue-router";
import { filesPathName } from "@/router";
import { membersKey, mutateCaseKey } from "@/logic/provide";
import FormSelect from "../FormSelect.vue";
import StakeholderPreview from "@/components/actors/StakeholderPreview.vue";
import FormInput from "../FormInput.vue";

const props = defineProps<{ modelValue: Case; openMode: OpenMode }>();
const emit = defineEmits<{
  "click:detail": [element: Case, offset: number];
  "click:cancel": [];
  "click:create": [element: CaseCreation];
}>();

const router = useRouter();
const editCase = inject(mutateCaseKey);
const members = inject(membersKey);

const editMode = ref(props.openMode === "Create");
const model: Ref<Case> = ref(caseCopy(props.modelValue));

watch(props, (newProps) => {
  if (editMode.value) {
    model.value.targets = newProps.modelValue.targets;
    model.value.sources = newProps.modelValue.sources;
    model.value.last_update = newProps.modelValue.last_update;
    return;
  }

  model.value = caseCopy(newProps.modelValue);
  editMode.value = newProps.openMode === "Create";
});

function cancel() {
  model.value = caseCopy(props.modelValue);
  editMode.value = false;
  emit("click:cancel");
}

function save() {
  if (editCase) {
    const patch = diff(props.modelValue, model.value);
    delete patch.last_update;
    delete patch.creation_date;
    if (Object.keys(patch).length !== 0) {
      editCase(model.value.id, patch);
    }
  }
  editMode.value = false;
}

function onCaseCreation() {
  var creation = { ...model.value } as any;
  delete creation.id;
  delete creation.last_update;
  delete creation.creation_date;

  emit("click:create", creation);
}

function toggleEdit() {
  editMode.value = !editMode.value;
}

function onOpen() {
  router.push({ name: filesPathName, params: { id: props.modelValue.id } });
}

function clickSelect(target: EventTarget | null) {
  if (!target) {
    return;
  }

  var me = target as HTMLElement;

  // Trigger a scroll into view when selected
  if (props.openMode === "Edit") {
    setTimeout(() => {
      me.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  }

  while (!me.classList.contains("file-container") && me.offsetParent) {
    me = me.offsetParent as HTMLElement;
  }

  emit("click:detail", model.value, me.offsetTop);
}

const sourcesValidation = computed(() => {
  if (!model.value.sources?.length) {
    return "Ce dossier devrait avoir au moins un requérant";
  }

  return "";
});

const targetsValidation = computed(() => {
  if (
    (model.value.case_type === "Plainte" ||
      model.value.case_type === "Différend" ||
      model.value.case_type === "Signalement") &&
    !model.value.targets?.length
  ) {
    return "Ce dossier devrait avoir au moins un mis en cause";
  }

  return "";
});

const statusValidation = computed(() => {
  if (
    model.value.case_status !== "Classé" &&
    model.value.case_status !== "Actif"
  ) {
    return "Ce dossier n'est ni classé, ni actif";
  }

  return "";
});

const receiptDateValidation = computed(() => {
  if (model.value.case_type === "Plainte" && !model.value.demand_receipt_date) {
    return "Date de réception manquante";
  }

  if (model.value.case_type === "Plainte" && model.value.demand_receipt_date) {
    const d = Date.parse(model.value.demand_receipt_date);
    if (d == 0 || d < 0) {
      return "Date de réception invalide";
    }
  }

  return "";
});

const statusClass = computed(() => {
  let result = {
    closed: false,
    open: false,
    warn: false,
    full: editMode.value,
  };
  if (model.value.case_status === "Classé") {
    result.closed = true;
  } else if (model.value.case_status === "Actif") {
    result.open = true;
  }

  if (
    targetsValidation.value ||
    statusValidation.value ||
    sourcesValidation.value ||
    receiptDateValidation.value
  ) {
    result.warn = true;
  }

  return result;
});

const type = computed(() => {
  const type = model.value.case_type || "Dossier sans classification";
  if (model.value.case_sub_type) {
    return { main: type + ": ", sub: model.value.case_sub_type };
  } else {
    return { main: type, sub: "" };
  }
});

const status = computed(() => {
  if (model.value.case_status === "Classé") {
    if (model.value.case_sub_status) {
      return model.value.case_sub_status + " (Classé)";
    } else {
      return "Dossier Classé";
    }
  } else if (model.value.case_status === "Actif") {
    return "Dossier Actif";
  }

  return "Statut Inconnu";
});

const creationDate = computed(() => {
  const d = Date.parse(model.value.creation_date);
  if (!d) {
    return "";
  }

  const date = new Date(d);
  return " | Ouvert le " + date.toLocaleDateString();
});

const documentReceiptDate = computed(() => {
  if (!model.value.demand_receipt_date) {
    return "";
  }

  const d = Date.parse(model.value.demand_receipt_date);
  if (!d) {
    return "";
  }

  const date = new Date(d);
  return " | Courrier du " + date.toLocaleDateString();
});

const memberName = computed(() => {
  if (!model.value.member_id) {
    return "";
  }

  return (
    members?.value.index.get(model.value.member_id)?.display_name ||
    "Ancien conseillé " + model.value.member_id
  );
});
</script>

<template>
  <div class="file-container" :class="statusClass">
    <div
      class="header-container"
      :class="{ pointer: props.openMode == 'Edit' }"
      @click.stop="clickSelect($event.target)"
    >
      <div class="d-header">
        <div class="spaceme">
          <h2>{{ type.main }}</h2>
          <span class="colored input-header-text">{{ type.sub }}</span>
        </div>
        <h3 class="no-color">{{ memberName }}</h3>
      </div>
    </div>
    <div class="content-container">
      <div v-if="editMode" class="actors-split">
        <div>
          <FormSelect
            label="Statut"
            v-model="model.case_status"
            :options="caseStatusOptions"
            :disabled="!editMode"
          />
        </div>
        <div v-if="model.case_status == 'Classé'">
          <FormSelect
            label="Détail"
            v-model="model.case_sub_status"
            :options="caseSubStatusOptions"
            :disabled="!editMode"
          />
        </div>
        <div v-else><span> </span></div>
      </div>
      <div v-if="editMode" class="actors-split">
        <div>
          <FormSelect
            label="Type de dossier"
            v-model="model.case_type"
            :options="caseTypeOptions"
            :disabled="!editMode"
          />
        </div>
        <div>
          <FormSelect
            label="Sous-type"
            v-model="model.case_sub_type"
            :options="caseSubTypeOptions"
            :disabled="!editMode"
          />
        </div>
      </div>
      <div v-if="editMode && members">
        <div class="actors-split">
          <div>
            <FormSelect
              label="Conseiller en charche du dossier"
              v-model="model.member_id"
              :options="members?.options"
              :disabled="!editMode"
            />
          </div>
          <div>
            <FormInput
              label="Date de réception de la demande"
              v-model="model.demand_receipt_date"
              type="date"
              :disabled="!editMode"
            />
          </div>
        </div>
      </div>

      <div class="actors-split">
        <div class="plaignant">
          <StakeholderPreview
            :label="model.sources?.length ? 'Requérants' : 'Requérant'"
            :warning="sourcesValidation"
            :model="model.sources || []"
          />
        </div>
        <div class="accused">
          <StakeholderPreview
            label="Mis en cause"
            :model="model.targets || []"
            :warning="targetsValidation"
          />
        </div>
      </div>
      <MDBTextarea
        label="Notes"
        v-model="model.description"
        :disabled="!editMode"
      />
    </div>
    <div class="content-footer">
      <div>
        <span v-if="statusValidation !== ''" class="status warn">{{
          statusValidation
        }}</span>
        <span v-else class="status">{{ status }}</span
        ><span>{{ creationDate }}</span>

        <span v-if="receiptDateValidation" class="status warn">
          | {{ receiptDateValidation }}</span
        >
        <span v-else>{{ documentReceiptDate }}</span>
      </div>
      <template v-if="openMode != 'ReadOnly'">
        <div v-if="openMode == 'Create'" class="buttons">
          <span class="button" @click.stop="cancel">{{ "Annuler" }}</span>
          <span class="button" @click.stop="onCaseCreation">{{
            "Enregistrer"
          }}</span>
        </div>
        <div v-else-if="editMode" class="buttons">
          <span class="button" @click.stop="cancel">{{ "Annuler" }}</span>
          <span class="button" @click.stop="save">{{ "Enregistrer" }}</span>
        </div>
        <div v-else class="buttons">
          <span class="button" @click.stop="toggleEdit">{{ "Editer" }}</span>
          <span
            v-if="openMode == 'InlineList'"
            class="button"
            @click.stop="onOpen"
            >{{ "Ouvrir ce dossier" }}</span
          >
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
h2 {
  display: inline-block;
  padding-right: 8px;
}

.entry {
  text-decoration: underline;
}

.file-container {
  display: flex;
  flex-direction: column;
  position: relative;
  width: 680px;
  min-height: 40px;
  margin-right: auto;

  border-radius: 0 6px 6px 0;
  border-left-width: 6px;
  border-left-style: solid;

  background-color: var(--color-background-soft);
  transition: all 0.1s ease-in-out;

  &.list:hover,
  &.selected {
    transition: transform 0.2s ease-in;
    transform: translateX(12px);
  }

  .pointer {
    cursor: pointer;
  }

  .header-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 56px;

    border-bottom-width: 6px;
    border-bottom-style: solid;
    border-color: inherit;
    white-space: nowrap;

    .d-header {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      align-items: baseline;

      width: 100%;
      margin-left: 1em;
      margin-right: 1em;
    }

    .spaceme {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: baseline;

      margin-right: 1em;
      margin-bottom: 5px;
      flex-wrap: wrap;

      span {
        margin-bottom: 5px; // when overflowing, this gets to the second line, and looks good
      }
    }
  }

  .content-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    padding: 1.5em 1em 1.5em 1em;
    row-gap: 1.3em;
  }

  .content-footer {
    display: flex;
    flex-direction: row;
    justify-content: space-between;

    font-style: normal;
    font-weight: 500;
    font-size: 13px;
    line-height: 16px;
    margin-left: 1em;
    margin-bottom: 1.25em;

    .buttons {
      span {
        margin-left: 1em;
      }
    }

    div:last-child {
      margin-right: 1em;
    }
  }

  .actors-split {
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: space-evenly;
    column-gap: 1.5em;

    div {
      width: 100%;
    }
  }
}

.closed {
  border-color: inherit;
}

.open {
  h2,
  h3,
  .colored {
    color: var(--vt-c-purple);
    &.no-color {
      color: inherit;
    }
  }

  .status {
    color: var(--vt-c-purple-light);
  }
  border-color: var(--vt-c-purple-light);
}
.warn {
  h2,
  h3,
  .colored {
    color: var(--vt-c-purple);
    &.no-color {
      color: inherit;
    }
  }
  border-color: var(--color-background-warning);
}

.status {
  &.warn {
    color: var(--color-background-warning);
  }
}
</style>
